import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, fmtDate, fmtTime, localDate, localToUtc, type RosterShiftDTO, type ShiftTemplateDTO } from '@em/shared';
import { api, qk, queryClient, useEmployees, useJobs, useSettings, useShiftTemplates, useSites, useTeams } from '../../api';
import { useSession } from '../../auth/AuthContext';
import { localHHMM, mondayOf, todayIn } from '../../components/format';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, ErrorAlert, Field, Input, Loading, PageHeader, QueryState, Select } from '../../components/ui';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function RostersPage() {
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const today = todayIn(tz);
  const toast = useToast();
  const [weekStart, setWeekStart] = useState(() => mondayOf(today));
  const [teamId, setTeamId] = useState('');
  const [editing, setEditing] = useState<{ shift?: RosterShiftDTO; employeeId: string; date: string } | null>(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const weekEnd = addDays(weekStart, 6);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const rosterQ = { from: weekStart, to: weekEnd, teamId: teamId || undefined };
  const roster = useQuery({ queryKey: qk.roster(rosterQ), queryFn: () => api.roster(rosterQ) });
  const employees = useEmployees({ status: 'active', teamId: teamId || undefined });
  const teams = useTeams();
  const sites = useSites();

  const cells = useMemo(() => {
    const m = new Map<string, RosterShiftDTO[]>();
    for (const s of roster.data ?? []) {
      const k = `${s.employeeId}|${localDate(s.startAt, tz)}`;
      m.set(k, [...(m.get(k) ?? []), s]);
    }
    return m;
  }, [roster.data, tz]);
  const drafts = (roster.data ?? []).filter((s) => s.status === 'draft').length;
  const siteName = (id: string | null) => (id ? sites.data?.find((s) => s.id === id)?.name : null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['roster'] });
    void queryClient.invalidateQueries({ queryKey: ['live'] });
    void queryClient.invalidateQueries({ queryKey: ['exceptions'] });
  };

  const copyLastWeek = async () => {
    setBusy('copy');
    try {
      const r = await api.copyRosterWeek({ fromWeekStart: addDays(weekStart, -7), toWeekStart: weekStart, teamId: teamId || undefined });
      refresh();
      toast.success(r.created ? `Copied ${r.created} shift${r.created === 1 ? '' : 's'} as drafts` : 'Last week had no shifts to copy');
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const publish = async () => {
    setBusy('publish');
    try {
      const r = await api.publishRoster({ from: weekStart, to: weekEnd, teamId: teamId || undefined });
      refresh();
      toast.success(`Published ${r.published} shift${r.published === 1 ? '' : 's'}`, `${r.notified} employee${r.notified === 1 ? '' : 's'} notified.`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Rosters"
        subtitle={`Week of ${fmtDate(weekStart)}. Times in ${tz.replace('Australia/', '')} time. Draft shifts are only visible to staff after you publish.`}
        actions={
          <>
            <Button onClick={() => setTemplatesOpen(true)}>Shift templates</Button>
            <Button onClick={copyLastWeek} loading={busy === 'copy'}>
              Copy last week
            </Button>
            <Button variant="primary" onClick={publish} loading={busy === 'publish'} disabled={drafts === 0}>
              Publish week{drafts ? ` (${drafts} draft${drafts === 1 ? '' : 's'})` : ''}
            </Button>
          </>
        }
      />
      <div className="filters">
        <div className="row">
          <Button onClick={() => setWeekStart(addDays(weekStart, -7))} aria-label="Previous week">
            ‹ Previous
          </Button>
          <Button onClick={() => setWeekStart(mondayOf(today))}>This week</Button>
          <Button onClick={() => setWeekStart(addDays(weekStart, 7))} aria-label="Next week">
            Next ›
          </Button>
        </div>
        <Field label="Week starting">
          <Input type="date" value={weekStart} onChange={(e) => e.target.value && setWeekStart(mondayOf(e.target.value))} />
        </Field>
        <Field label="Team">
          <Select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            <option value="">All teams</option>
            {teams.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="legend" style={{ paddingBottom: 8 }}>
          <span>
            <span className="swatch" />
            Published
          </span>
          <span>
            <span className="swatch draft" />
            Draft
          </span>
        </div>
      </div>

      <Card tight>
        {roster.isPending || employees.isPending ? (
          <Loading label="Loading roster" />
        ) : roster.isError || employees.isError ? (
          <div className="card-body">
            <ErrorAlert error={roster.error ?? employees.error} />
          </div>
        ) : employees.data!.length === 0 ? (
          <EmptyState title="No active employees in this team" />
        ) : (
          <div className="table-wrap">
            <table className="roster-grid">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Employee</th>
                  {days.map((d, i) => (
                    <th key={d} className={d === today ? 'today' : ''}>
                      {DAY_NAMES[i]} {Number(d.slice(8))}
                      {d === today && ' · today'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.data!.map((e) => (
                  <tr key={e.id}>
                    <td className="name">
                      {e.displayName}
                      <div className="small muted">{e.teamName ?? 'No team'}</div>
                    </td>
                    {days.map((d) => {
                      const shifts = cells.get(`${e.id}|${d}`) ?? [];
                      return (
                        <td
                          key={d}
                          className="cell"
                          tabIndex={0}
                          aria-label={`Add a shift for ${e.displayName} on ${fmtDate(d)}`}
                          onClick={() => setEditing({ employeeId: e.id, date: d })}
                          onKeyDown={(ev) => {
                            if (ev.target === ev.currentTarget && (ev.key === 'Enter' || ev.key === ' ')) {
                              ev.preventDefault();
                              setEditing({ employeeId: e.id, date: d });
                            }
                          }}
                        >
                          {shifts.map((s) => (
                            <button
                              key={s.id}
                              className={`shift-chip ${s.status === 'draft' ? 'draft' : ''}`}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setEditing({ shift: s, employeeId: e.id, date: d });
                              }}
                              aria-label={`${s.status === 'draft' ? 'Draft' : 'Published'} shift ${fmtTime(s.startAt, tz, false)}, edit`}
                            >
                              {fmtTime(s.startAt, tz, false)}–{s.endAt ? fmtTime(s.endAt, tz, false) : 'open'}
                              <small>
                                {siteName(s.siteId) ?? 'No site'}
                                {s.status === 'draft' ? ' · draft' : ''}
                              </small>
                            </button>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ShiftDialog
          tz={tz}
          employeeName={employees.data?.find((e) => e.id === editing.employeeId)?.displayName ?? ''}
          {...editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
      {templatesOpen && <TemplatesPanel onClose={() => setTemplatesOpen(false)} />}
    </>
  );
}

function ShiftDialog({
  shift,
  employeeId,
  employeeName,
  date,
  tz,
  onClose,
  onSaved,
}: {
  shift?: RosterShiftDTO;
  employeeId: string;
  employeeName: string;
  date: string;
  tz: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const session = useSession();
  const templates = useShiftTemplates();
  const sites = useSites();
  const jobs = useJobs(session.features.jobCosting);
  const toast = useToast();
  const [start, setStart] = useState(shift ? localHHMM(shift.startAt, tz) : '07:00');
  const [end, setEnd] = useState(shift?.endAt ? localHHMM(shift.endAt, tz) : '15:30');
  const [breakMinutes, setBreakMinutes] = useState(String(shift?.breakMinutes ?? 30));
  const [siteId, setSiteId] = useState(shift?.siteId ?? '');
  const [jobId, setJobId] = useState(shift?.jobId ?? '');
  const [note, setNote] = useState(shift?.note ?? '');
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const overnight = !!end && end <= start;

  const applyTemplate = (t: ShiftTemplateDTO | undefined) => {
    if (!t) return;
    setStart(t.startTime);
    setEnd(t.endTime ?? '');
    setBreakMinutes(String(t.breakMinutes));
    if (t.siteId) setSiteId(t.siteId);
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const startAt = localToUtc(date, start, tz);
      const endAt = end ? localToUtc(overnight ? addDays(date, 1) : date, end, tz) : null;
      const body = { employeeId, startAt, endAt, breakMinutes: Number(breakMinutes) || 0, siteId: siteId || null, note: note.trim() || null, jobId: jobId || null };
      if (shift) await api.updateRosterShift(shift.id, body);
      else await api.createRosterShift(body);
      onSaved();
      toast.success(shift ? 'Shift updated' : 'Shift added as a draft', shift?.status === 'published' ? `${employeeName} has been notified.` : undefined);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    if (!shift) return;
    setBusy('delete');
    try {
      await api.deleteRosterShift(shift.id);
      onSaved();
      toast.success('Shift removed');
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal
      title={`${shift ? 'Edit shift' : 'Add shift'}: ${employeeName}`}
      onClose={onClose}
      onSubmit={save}
      footer={
        <>
          {shift && (
            <Button variant="danger" onClick={remove} loading={busy === 'delete'} style={{ marginRight: 'auto' }}>
              Delete shift
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={busy === 'save'} disabled={!start}>
            {shift ? 'Save' : 'Add shift'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="row">
          <strong>{fmtDate(date)}</strong>
          {shift && <Badge tone={shift.status === 'draft' ? 'grey' : 'accent'}>{shift.status === 'draft' ? 'Draft' : 'Published'}</Badge>}
        </div>
        {shift?.status === 'published' && <Alert tone="accent">This shift is published. Changes notify the employee and recalculate their timesheet.</Alert>}
        <ErrorAlert error={error} />
        <Field label="Shift template" hint="Fills in the times below; adjust them if needed.">
          <Select
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              applyTemplate(templates.data?.find((t) => t.id === e.target.value));
            }}
          >
            <option value="">Custom times</option>
            {templates.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.startTime}–{t.endTime ?? 'open'})
              </option>
            ))}
          </Select>
        </Field>
        <div className="form-grid three">
          <Field label="Start" required>
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} required />
          </Field>
          <Field label="End" hint={overnight ? 'Finishes the next day' : 'Leave empty for an open finish'}>
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Field>
          <Field label="Break (minutes)">
            <Input type="number" min={0} max={240} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="Site">
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">No site</option>
              {sites.data
                ?.filter((s) => s.active || s.id === siteId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </Select>
          </Field>
          {session.features.jobCosting && (
            <Field label="Job">
              <Select value={jobId} onChange={(e) => setJobId(e.target.value)}>
                <option value="">No job</option>
                {jobs.data
                  ?.filter((j) => j.status === 'active' || j.id === jobId)
                  .map((j) => (
                    <option key={j.id} value={j.id}>
                      {j.code} {j.name}
                    </option>
                  ))}
              </Select>
            </Field>
          )}
          <Field label="Note" className="full">
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

function TemplatesPanel({ onClose }: { onClose: () => void }) {
  const q = useShiftTemplates();
  const sites = useSites();
  const toast = useToast();
  const [form, setForm] = useState<Omit<ShiftTemplateDTO, 'id'> & { id?: string }>({ name: '', startTime: '07:00', endTime: '15:30', breakMinutes: 30, siteId: null });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const reload = () => queryClient.invalidateQueries({ queryKey: qk.shiftTemplates });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { id, ...b } = form;
      if (id) await api.updateShiftTemplate(id, b);
      else await api.createShiftTemplate(b);
      await reload();
      toast.success(id ? 'Template saved' : 'Template added');
      setForm({ name: '', startTime: '07:00', endTime: '15:30', breakMinutes: 30, siteId: null });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Shift templates" onClose={onClose} wide>
      <div className="stack">
        <QueryState q={q}>
          {(list) =>
            list.length === 0 ? (
              <EmptyState title="No shift templates yet" />
            ) : (
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Times</th>
                    <th className="num">Break</th>
                    <th>Site</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {list.map((t) => (
                    <tr key={t.id}>
                      <td className="strong">{t.name}</td>
                      <td>
                        {t.startTime}–{t.endTime ?? 'open'}
                      </td>
                      <td className="num">{t.breakMinutes} min</td>
                      <td>{sites.data?.find((s) => s.id === t.siteId)?.name ?? '–'}</td>
                      <td className="actions">
                        <Button size="sm" onClick={() => setForm({ ...t })}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={async () => {
                            try {
                              await api.deleteShiftTemplate(t.id);
                              await reload();
                              toast.success('Template deleted');
                            } catch (e) {
                              toast.error(e);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </QueryState>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <fieldset>
            <legend>{form.id ? 'Edit template' : 'New template'}</legend>
            <div className="stack">
              <ErrorAlert error={error} />
              <div className="form-grid">
                <Field label="Name" required>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </Field>
                <Field label="Site">
                  <Select value={form.siteId ?? ''} onChange={(e) => setForm({ ...form, siteId: e.target.value || null })}>
                    <option value="">Any site</option>
                    {sites.data?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="form-grid three">
                <Field label="Start" required>
                  <Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} required />
                </Field>
                <Field label="End">
                  <Input type="time" value={form.endTime ?? ''} onChange={(e) => setForm({ ...form, endTime: e.target.value || null })} />
                </Field>
                <Field label="Break (minutes)">
                  <Input type="number" min={0} max={240} value={form.breakMinutes} onChange={(e) => setForm({ ...form, breakMinutes: Number(e.target.value) || 0 })} />
                </Field>
              </div>
              <div className="row end">
                {form.id && (
                  <Button onClick={() => setForm({ name: '', startTime: '07:00', endTime: '15:30', breakMinutes: 30, siteId: null })}>Cancel edit</Button>
                )}
                <Button type="submit" variant="primary" loading={busy} disabled={!form.name.trim()}>
                  {form.id ? 'Save template' : 'Add template'}
                </Button>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
    </Modal>
  );
}
